/**
 * Device detection + per-profile MediaPipe tuning.
 *
 * iPadOS 13+ reports userAgent "Macintosh" by default. Detection uses
 * `ontouchend` + `maxTouchPoints` to disambiguate. iPhone and iPad get
 * different perf profiles — iPad A12+ GPUs handle WebGL well, while
 * iPhone WebGL delegate in Safari is often slower than CPU.
 */

const ua = (): string =>
  typeof navigator !== 'undefined' ? navigator.userAgent : '';

export const isIPhone = (): boolean => /iPhone|iPod/i.test(ua());

export const isIPad = (): boolean => {
  const s = ua();
  if (/iPad/i.test(s)) return true;
  // iPadOS masquerades as Macintosh. Real Macs don't have touch.
  return (
    s.includes('Macintosh') &&
    typeof document !== 'undefined' &&
    'ontouchend' in document &&
    typeof navigator !== 'undefined' &&
    navigator.maxTouchPoints > 1
  );
};

export const isIOS = (): boolean => isIPhone() || isIPad();

export const isAndroid = (): boolean => /Android/i.test(ua());

export const isMobile = (): boolean =>
  isIPhone() || isIPad() || isAndroid() || /Mobi/i.test(ua());

export const isSafari = (): boolean =>
  typeof navigator !== 'undefined' &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export interface DevicePerfProfile {
  cameraWidth: number;
  cameraHeight: number;
  delegate: 'GPU' | 'CPU';
  numHands: number;
}

export const getPerfProfile = (): DevicePerfProfile => {
  if (isIPhone()) {
    return { cameraWidth: 640, cameraHeight: 480, delegate: 'CPU', numHands: 2 };
  }
  if (isIPad()) {
    // iPad A12+ could run WebGL, but Safari's WebGL delegate
    // for HandLandmarker is frequently broken. CPU + WASM SIMD
    // is the reliable path — slower but it actually works.
    return { cameraWidth: 640, cameraHeight: 480, delegate: 'CPU', numHands: 2 };
  }
  if (isAndroid()) {
    return { cameraWidth: 640, cameraHeight: 480, delegate: 'GPU', numHands: 2 };
  }
  return { cameraWidth: 1280, cameraHeight: 720, delegate: 'GPU', numHands: 2 };
};
